import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import type { GeneralFormData, SubmitDeptConfigResult } from "../types";
import { useAirtableConfigPreview, useVerifyAirtableConfig } from "./airtableImportHooks";
import { airtableErrorMessage, isRecordChangedError } from "./airtableImportLabels";
import { mergeAcceptedRows } from "./airtableImportMerge";
import type { AirtableFacilityOption } from "./airtableImportTypes";
import { acceptedWageRuleKeys, useSaveAcceptedWageRules, wageRuleReasons } from "./airtableWageRules";
import type { AirtableReviewState } from "./useAirtableReview";

type Params = {
	review: AirtableReviewState;
	formData: GeneralFormData;
	onApply: (merged: GeneralFormData) => Promise<SubmitDeptConfigResult>;
	onClose: () => void;
	/** Advance the wizard once a preview is actually in hand. */
	onPreviewLoaded: () => void;
};

/**
 * The network half of the Airtable import: preview, then the verify-and-apply sequence.
 *
 * Analytics follow the repo's async lifecycle vocabulary (`AGENTS.md`): snake_case
 * `<operation>_start` before each request, `_success` / `_error` on settlement, for all three
 * operations. Three separate operations rather than one, because they fail for different reasons and
 * a funnel that cannot tell "the record moved" (verify) from "the config refused to save" (save)
 * cannot be acted on.
 *
 * Split out of `AirtableImportWizard` because it is the only genuinely intricate part of this flow —
 * four outcomes, three of which are refusals — and it was crowding out the step definitions in a file
 * that is otherwise a list of steps.
 */
export function useAirtableImportFlow({ review, formData, onApply, onClose, onPreviewLoaded }: Params) {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const { countsByKind, acceptedRows, applyDisabledReasons, loadDiff, loadDiffAfterRefusal } = review;

	const preview = useAirtableConfigPreview();
	const verify = useVerifyAirtableConfig();
	const saveWageRules = useSaveAcceptedWageRules();

	const [fingerprint, setFingerprint] = useState<string | null>(null);
	const [duplicateRecordIds, setDuplicateRecordIds] = useState<string[]>([]);
	const [duplicatesTruncated, setDuplicatesTruncated] = useState(false);
	const [stepError, setStepError] = useState<string | null>(null);
	const [staleRecordMessage, setStaleRecordMessage] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	const translateError = useCallback(
		(error: unknown) => {
			const { key, params } = airtableErrorMessage(error);
			return t(key, params);
		},
		[t],
	);

	/** Counts by kind, flattened into analytics-safe scalar props. */
	const kindProps = useMemo(
		() => ({
			rows_deterministic: countsByKind.deterministic,
			rows_llm: countsByKind.llm,
			rows_unresolvable: countsByKind.unresolvable,
			rows_out_of_band: countsByKind.out_of_band,
			rows_blocked: countsByKind.blocked,
		}),
		[countsByKind],
	);

	const runPreview = useCallback(
		(recordId: string) => {
			setStepError(null);
			track("airtable_config_preview_start", { department_id: chosenDepartment.department_id, airtable_record_id: recordId });
			preview.mutate(
				{ airtableRecordId: recordId },
				{
					onSuccess: (data) => {
						loadDiff(data.rows);
						setFingerprint(data.recordFingerprint);
						setDuplicateRecordIds(data.duplicateRecordIds);
						setDuplicatesTruncated(data.duplicateRecordIdsTruncated);
						onPreviewLoaded();
						track("airtable_config_preview_success", {
							department_id: chosenDepartment.department_id,
							airtable_record_id: recordId,
							row_count: data.rows.length,
						});
					},
					onError: (error) => {
						trackError("airtable_config_preview_error", {
							department_id: chosenDepartment.department_id,
							airtable_record_id: recordId,
							reason: airtableErrorMessage(error).key,
						});
						setStepError(translateError(error));
					},
				},
			);
		},
		[preview, loadDiff, onPreviewLoaded, track, trackError, translateError, chosenDepartment.department_id],
	);

	/**
	 * The record moved under the reviewer. Re-read it, show the FRESH diff, and drop every accept
	 * decision: carrying them over would approve text nobody has read, which is the one failure this
	 * flow is built to prevent.
	 */
	const reloadAfterRecordMoved = useCallback(
		(recordId: string, message: string) => {
			setStaleRecordMessage(message);
			preview.mutate(
				{ airtableRecordId: recordId },
				{
					onSuccess: (data) => {
						loadDiffAfterRefusal(data.rows);
						setFingerprint(data.recordFingerprint);
						setDuplicateRecordIds(data.duplicateRecordIds);
						setDuplicatesTruncated(data.duplicateRecordIdsTruncated);
					},
					onError: (refreshError) => setStepError(translateError(refreshError)),
				},
			);
		},
		[preview, loadDiffAfterRefusal, translateError],
	);

	const runApply = useCallback(
		(facility: AirtableFacilityOption) => {
			if (!fingerprint || applyDisabledReasons.length > 0) return;
			setStepError(null);
			setStaleRecordMessage(null);
			const acceptedFieldKeys = acceptedRows.map(({ row }) => row.fieldKey);
			track("airtable_config_verify_start", { department_id: chosenDepartment.department_id, airtable_record_id: facility.recordId });

			verify.mutate(
				{ airtableRecordId: facility.recordId, expectedFingerprint: fingerprint },
				{
					onSuccess: (fresh) => {
						setDuplicateRecordIds(fresh.duplicateRecordIds);
						setDuplicatesTruncated(fresh.duplicateRecordIdsTruncated);
						// Belt to the server's braces. The contract is that a moved record is REFUSED, but a 200
						// whose fingerprint differs from the one reviewed means the same thing, and trusting the
						// status code alone would apply decisions made against older text.
						if (fresh.recordFingerprint !== fingerprint) {
							trackError("airtable_config_verify_error", {
								department_id: chosenDepartment.department_id,
								airtable_record_id: facility.recordId,
								reason: "fingerprint_mismatch",
							});
							reloadAfterRecordMoved(facility.recordId, t("department.config.airtable.stale.body"));
							return;
						}
						// The merge is handed over and AWAITED. Firing success before the save resolves was wrong
						// twice over: the department config short-circuits silently when the merged form trips a
						// validator, so the dialog closed, the acceptances went with it, nothing was written, and
						// the event said it had been.
						track("airtable_config_verify_success", {
							department_id: chosenDepartment.department_id,
							airtable_record_id: facility.recordId,
						});
						const merged = mergeAcceptedRows(formData, acceptedRows);
						const wageRuleKeys = acceptedWageRuleKeys(acceptedRows);
						// Refused before anything is written, so a bad rule row cannot leave the config saved and
						// the wage tables not.
						const invalidWageRules = wageRuleReasons(merged, wageRuleKeys);
						if (invalidWageRules.length > 0) {
							trackError("airtable_config_save_error", {
								department_id: chosenDepartment.department_id,
								airtable_record_id: facility.recordId,
								reason: invalidWageRules.join(","),
							});
							setStepError(t("department.config.airtable.error.wageRulesInvalid", { reasons: invalidWageRules.map((reason) => t(reason)).join(" ") }));
							return;
						}
						setIsSaving(true);
						track("airtable_config_save_start", {
							department_id: chosenDepartment.department_id,
							airtable_record_id: facility.recordId,
							accepted_field_keys: acceptedFieldKeys.join(","),
							accepted_count: acceptedFieldKeys.length,
							...kindProps,
						});
						void onApply(merged)
							.then(async ({ saved, uiErrorKey }) => {
								if (!saved) {
									trackError("airtable_config_save_error", {
										department_id: chosenDepartment.department_id,
										airtable_record_id: facility.recordId,
										reason: uiErrorKey ?? "save_refused",
									});
									// A named refusal from the config endpoint (a 409 such as the timezone lock)
									// renders its own sentence rather than "the save failed". The generic sentence is
									// for the refusals that genuinely have no key: the validator short-circuits,
									// whose causes are listed underneath it from `saveBlockedReasons`.
									setStepError(uiErrorKey ? t(uiErrorKey) : t("department.config.airtable.error.saveRefused"));
									return;
								}
								const unsavedWageRules = await saveWageRules(merged, wageRuleKeys);
								if (unsavedWageRules.length > 0) {
									trackError("airtable_config_save_error", {
										department_id: chosenDepartment.department_id,
										airtable_record_id: facility.recordId,
										reason: `wage_rules_not_saved:${unsavedWageRules.join(",")}`,
									});
									// The department config DID save, so this is not "nothing was written". Staying open
									// keeps the acceptances, and applying again re-sends only what is still different.
									setStepError(
										t("department.config.airtable.error.wageRulesNotSaved", {
											tables: unsavedWageRules.map((key) => t(`department.config.airtable.wageRuleTable.${key}`)).join(", "),
										}),
									);
									return;
								}
								track("airtable_config_save_success", {
									department_id: chosenDepartment.department_id,
									airtable_record_id: facility.recordId,
									accepted_field_keys: acceptedFieldKeys.join(","),
									accepted_count: acceptedFieldKeys.length,
									...kindProps,
								});
								onClose();
							})
							.finally(() => setIsSaving(false));
					},
					onError: (error) => {
						trackError("airtable_config_verify_error", {
							department_id: chosenDepartment.department_id,
							airtable_record_id: facility.recordId,
							reason: airtableErrorMessage(error).key,
						});
						if (!isRecordChangedError(error)) {
							setStepError(translateError(error));
							return;
						}
						reloadAfterRecordMoved(facility.recordId, translateError(error));
					},
				},
			);
		},
		[
			fingerprint,
			applyDisabledReasons,
			acceptedRows,
			verify,
			reloadAfterRecordMoved,
			formData,
			onApply,
			onClose,
			saveWageRules,
			track,
			trackError,
			translateError,
			kindProps,
			chosenDepartment.department_id,
			t,
		],
	);

	return {
		runPreview,
		runApply,
		duplicateRecordIds,
		duplicatesTruncated,
		stepError,
		setStepError,
		staleRecordMessage,
		isPreviewing: preview.isPending,
		isVerifying: verify.isPending,
		isSaving,
	};
}
